import { queryClient, queryClientKeys } from "$root/third-party-integrations/react-query/typedQueryClient";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { noop } from "@mdotm/shadcn/legacy/utils";
import {
	Button,
	DropdownMenu,
	type DropdownDropdownItem,
	type DropdownTriggerProps,
} from "@mdotm/shadcn/mdotm/components";
import { asyncStateFrom } from "@mdotm/utils/promise";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { instrumentsRepository } from "../../instruments/repository";
import { useCompositionHeaderAction } from "../builder";
import type { InstrumentCompositionEditorProps } from "../instrumentEditor.types";
import type { AddMenu } from "./quickActions";

function AddInstrumentsTrigger({ children, state, ...props }: DropdownTriggerProps & { children?: React.ReactNode }) {
	return (
		<Button
			state={asyncStateFrom(state)}
			progressBar={false}
			palette="secondary"
			size="small"
			className="inline-flex gap-2"
			data-qualifier="CompositionEditor/HeaderAction/DropdownMenu"
			{...props}
		>
			<Icon icon="add-ptf" size={18} />
			{children}
		</Button>
	);
}

/**
 * The Add menu, built from the action names the entity's configuration lists — which ones an entity
 * gets, and in which order, is decided in `quickActions.ts`. An empty list renders the trigger
 * disabled.
 */
export function AddInstrumentsMenu(
	props: InstrumentCompositionEditorProps & { actionKeys: AddMenu },
): React.JSX.Element {
	const { instrumentBuilder, entity, actionKeys } = props;
	const { t } = useTranslation();
	const compositionHeaderAction = useCompositionHeaderAction({
		instrumentBuilder,
		quantityBuilder: props.compositionMode === "quantity" ? props.instrumentBuilder : undefined,
		entity,
		uuid: props.mode === "edit" ? props.uuid : undefined,
	});

	// The pickers behind these actions are cached across mounts, and a composition edited elsewhere
	// in the meantime leaves them stale — so the menu drops and refetches them as it arrives.
	useEffect(() => {
		queryClient
			.invalidateQueries({
				predicate(query) {
					const queryKey = query.queryKey?.[0];
					return (
						queryKey === queryClientKeys.queryInstrumentsAddableInComposition ||
						queryKey === queryClientKeys.queryAddInstrumentsEntitySummary ||
						queryKey === queryClientKeys.querySelectableInstrumentBaskets
					);
				},
			})
			.then(async () => {
				await queryClient.prefetchQuery(instrumentsRepository.standardClassifications.getQueryConfig());
				await queryClient.refetchQueries({
					predicate(query) {
						const queryKey = query.queryKey?.[0];
						return (
							queryKey === queryClientKeys.queryInstrumentsAddableInComposition ||
							queryKey === queryClientKeys.queryAddInstrumentsEntitySummary ||
							queryKey === queryClientKeys.querySelectableInstrumentBaskets
						);
					},
				});
			})
			.catch(noop);
	}, []);

	const actions = useMemo(
		(): DropdownDropdownItem[] => actionKeys.map((key) => compositionHeaderAction[key]()),
		[compositionHeaderAction, actionKeys],
	);

	return (
		<div className="flex gap-2">
			<DropdownMenu
				Trigger={AddInstrumentsTrigger}
				extraTriggerProps={{ children: t("BUTTON.ADD") }}
				actions={actions}
			/>
		</div>
	);
}
